<template>
    <v-layout
        class="fill-height"
    >
        <v-navigation-drawer
            class="filter-navigation pt-16"
            :permanent="!$vuetify.breakpoint.smAndDown"
            :absolute="$vuetify.breakpoint.smAndDown"
            v-model="filterDrawer"
            width="300"
        >
            <form
                class="pa-4"
            >
                <v-switch
                    v-model="filter.showPast"
                    :label="$t('tourbook.filter.showPast')"
                />
                <v-btn
                    @click="resetFilter"
                >
                    {{ $t('action.reset') }}
                </v-btn>
            </form>
        </v-navigation-drawer>
        <v-container
            fluid
            class="pt-16"
        >
            <v-row>
                <v-col
                    cols="12"
                >
                    <v-data-table
                        :headers="headers"
                        :items="items"
                        :loading="isLoading"
                        :server-items-length="total"
                        :options.sync="options"
                        :footer-props="{itemsPerPageOptions: [10, 15, 30]}"
                    >
                        <template
                            v-slot:item.released="{item}"
                        >
                            <v-badge
                                :color="getStatusColor(item)"
                                :title="getStatusTitle(item)"
                            />
                        </template>
                        <template
                            v-slot:item.start="{item}"
                        >
                            <span>
                                {{ new Date(item.start).toLocaleDateString() }}
                            </span>
                        </template>
                        <template
                            v-slot:item.end="{item}"
                        >
                            <span>
                                {{ new Date(item.start).toLocaleDateString() }}
                            </span>
                        </template>
                        <template
                            v-slot:item.releasedDate="{item}"
                        >
                            <span
                                v-if="item.releasedDate"
                            >
                                {{ new Date(item.releasedDate).toLocaleDateString() }}
                            </span>
                            <span
                                v-else
                            >
                                {{ $t('notAvailable') }}
                            </span>
                        </template>
                        <template
                            v-slot:item.actions="{item}"
                        >
                            <v-btn
                                icon
                                download
                                target="_blank"
                                :href="`https://www.asi-reisen.de/web2print/tourbook/${item.travelCode}/${item.start}?pdf=1&u=true`"
                            >
                                <v-icon>
                                    mdi-cloud-download
                                </v-icon>
                            </v-btn>
                            <v-menu
                                v-if="areActionsAvailable(item)"
                            >
                                <template
                                    v-slot:activator="{ on, attrs }"
                                >
                                    <v-btn
                                        icon
                                        v-bind="attrs"
                                        v-on="on"
                                    >
                                        <v-icon>
                                            mdi-dots-vertical
                                        </v-icon>
                                    </v-btn>
                                </template>
                                <v-list
                                    dense
                                >
                                    <v-dialog
                                        v-model="acceptDialog"
                                        persistent
                                        max-width="290"
                                        v-if="!item.releaseChanges"
                                    >
                                        <template
                                            v-slot:activator="{ on, attrs }"
                                        >
                                            <v-list-item
                                                link
                                                v-bind="attrs"
                                                v-on="on"
                                                :disabled="isLoading"
                                            >
                                                <v-list-item-icon>
                                                    <v-icon
                                                        color="green"
                                                    >
                                                        mdi-check
                                                    </v-icon>
                                                </v-list-item-icon>
                                                <v-list-item-title>
                                                    {{ $t('tourbook.action.accept') }}
                                                </v-list-item-title>
                                            </v-list-item>
                                        </template>
                                        <v-card
                                            :loading="isLoading"
                                        >
                                            <v-card-title
                                                class="text-h5"
                                            >
                                                {{ $t('tourbook.accept.title') }}
                                            </v-card-title>
                                            <v-card-actions>
                                                <v-spacer/>
                                                <v-btn
                                                    color="error"
                                                    text
                                                    @click="acceptDialog = false"
                                                >
                                                    {{ $t('action.cancel') }}
                                                </v-btn>
                                                <v-btn
                                                    color="success"
                                                    text
                                                    @click="release(item)"
                                                >
                                                    {{ $t('action.ok') }}
                                                </v-btn>
                                            </v-card-actions>
                                        </v-card>
                                    </v-dialog>
                                    <v-list-item
                                        link
                                        @click="applyChanges(item)"
                                        :disabled="isLoading"
                                    >
                                        <v-list-item-icon>
                                            <v-icon
                                                color="orange"
                                            >
                                                mdi-pencil
                                            </v-icon>
                                        </v-list-item-icon>
                                        <v-list-item-title>
                                            {{ $t('tourbook.action.applyChanges') }}
                                        </v-list-item-title>
                                    </v-list-item>
                                </v-list>
                            </v-menu>
                        </template>
                    </v-data-table>
                </v-col>
            </v-row>
            <change-form
                v-model="changeActive"
                :schedule="changeSchedule"
            />
        </v-container>
    </v-layout>
</template>

<script>
import {mapActions, mapGetters} from "vuex";
import {TourbookAction} from "@/store/tourbook/types";
import {debounce} from "debounce";
import ChangeForm from "@/components/Tourbook/ChangeForm";

export default {
    components: {
        ChangeForm,
    },
    mounted() {
        this.loadTourbooksDebounce = debounce(this.loadTourbooks, 500);
    },
    data: () => ({
        filterDrawer: false,
        options: {
            sortBy: ['start'],
            sortDesc: [false],
            page: 1,
            itemsPerPage: 10,
        },
        filter: {
            showPast: false,
        },
        changeActive: false,
        changeSchedule: {},
        acceptDialog: false,
    }),
    watch: {
        filter: {
            handler() {
                this.loadTourbooksDebounce();
            },
            deep: true,
        },
        options: {
            handler() {
                this.loadTourbooksDebounce();
            },
            deep: true,
        },
    },
    computed: {
        ...mapGetters({
            items: 'tourbook/items',
            total: 'tourbook/total',
            isLoading: 'tourbook/isLoading',
        }),
        headers: function () {
            return [
                {
                    text: this.$t('tourbook.table.header.released'),
                    value: 'released'
                },
                {
                    text: this.$t('tourbook.table.header.travelCode'),
                    value: 'travelCode',
                },
                {
                    text: this.$t('tourbook.table.header.start'),
                    value: 'start',
                },
                {
                    text: this.$t('tourbook.table.header.end'),
                    value: 'end',
                },
                {
                    text: this.$t('tourbook.table.header.releasedDate'),
                    value: 'releasedDate',
                },
                {
                    text: '',
                    value: 'actions',
                    sortable: false,
                },
            ];
        },
    },
    methods: {
        ...mapActions({
            load: `tourbook/${TourbookAction.LOAD}`,
            releaseTourbook: `tourbook/${TourbookAction.RELEASE}`,
        }),
        loadTourbooks: function() {
            this.load({
                filter: this.filter,
                options: this.options,
            });
        },
        release: function (item) {
            this.releaseTourbook(item)
                .then(() => {
                    this.acceptDialog = false;
                });
        },
        applyChanges: function (item) {
            this.changeSchedule = item;
            this.changeActive = true;
        },
        resetFilter: function () {
            this.filter.showPast = false;
        },
        getStatusColor: function(item) {
            if(
                item.released
            ) {
                return 'green';
            }

            if(
                item.releaseChanges
            ) {
                return 'orange';
            }

            return 'red';
        },
        getStatusTitle: function(item) {
            if(
                item.released
            ) {
                return this.$t('tourbook.status.released');
            }

            if(
                item.releaseChanges
            ) {
                return this.$t('tourbook.status.pending');
            }

            return this.$t('tourbook.status.open');
        },
        areActionsAvailable: function(item) {
            return !item.released && (new Date(item.start) > new Date());
        },
    },
}
</script>

<style lang="scss" scoped>
.filter-navigation {
    background-color: #F3F3F3;
}
</style>
